import { AssetRef, FeedbackType, TypeArray } from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getArrayTypeReducerGuard
} from "../reducers"

export type Feedback = {
  type: FeedbackType
  text: string
  deltas: any
  assets: Array<AssetRef>
  enable_replay: boolean
  autoplay?: boolean
  showCaptions?: boolean
  videoTitle?: string
  answerIdentifier?: string // the id to reference for block_qti_interactions (since it won't be on the qti answer)
  feedbackType?: "correct" | "incorrect" | "neutral" | null
}

const reducerAction = {
  type: actionCreatorTypeGuard<Feedback, FeedbackType>("type", "Feedback"),
  text: actionCreatorTypeGuard<Feedback, string>("text", "Feedback"),
  deltas: actionCreatorTypeGuard<Feedback, any>("deltas", "Feedback"),
  assets: actionCreatorTypeGuard<Feedback, Array<AssetRef>>(
    "assets",
    "Feedback"
  ),
  enable_replay: actionCreatorTypeGuard<Feedback, boolean>(
    "enable_replay",
    "Feedback"
  ),
  autoplay: actionCreatorTypeGuard<Feedback, boolean>("autoplay", "Feedback"),
  showCaptions: actionCreatorTypeGuard<Feedback, boolean>(
    "showCaptions",
    "Feedback"
  ),
  videoTitle: actionCreatorTypeGuard<Feedback, string>(
    "videoTitle",
    "Feedback"
  ),
  answerIdentifier: actionCreatorTypeGuard<Feedback, string>(
    "answerIdentifier",
    "Feedback"
  ),
  feedbackType: actionCreatorTypeGuard<
    Feedback,
    "correct" | "incorrect" | "neutral" | null
  >("feedbackType", "Feedback"),
  __record: actionCreatorTypeGuard<TypeArray, Feedback>("Feedback", "__record"),
  __create: actionCreatorTypeGuard<TypeArray, null | Partial<Feedback>>(
    "create",
    "__create/Feedback"
  ),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Feedback
    | "__record"
    | "__delete"
    | "__create"]: key extends keyof typeof reducerAction
    ? (typeof reducerAction)[key][0]
    : never
}

// Our exported actions
export const feedbackAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const feedbackReducer = reducerTypeGuard<Feedback>()
  .case(...reducerAction.type)
  .case(...reducerAction.text)
  .case(...reducerAction.deltas)
  .case(...reducerAction.assets)
  .case(...reducerAction.enable_replay)
  .case(...reducerAction.autoplay)
  .case(...reducerAction.showCaptions)
  .case(...reducerAction.videoTitle)
  .case(...reducerAction.answerIdentifier)
  .case(...reducerAction.feedbackType)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "assets":
        return getArrayTypeReducerGuard<Feedback, AssetRef>(
          "feedbacks",
          "assets",
          "AssetRef",
          prevState,
          action
        )
    }

    return prevState
  })
